import { logEvent } from 'infrastructure/sora/logging';

export class Peer {
    private readonly defaultConfig = {}; // { iceServers: [{ 'urls': 'stun:stun.l.google.com:19302' }] }

    private readonly peer: RTCPeerConnection;
    private readonly candidateListeners: ((
        candidate: RTCIceCandidate
    ) => void)[] = [];
    private readonly streamListeners: ((stream: MediaStream, kind: string) => void)[] = [];

    constructor(config: RTCConfiguration) {
        const peer = new RTCPeerConnection({
            ...this.defaultConfig,
            ...config,
        });
        this.peer = peer;

        peer.onconnectionstatechange = logEvent(
            'onconnectionstatechange',
            () => peer.connectionState
        );
        peer.onicecandidateerror = logEvent(
            'onicecandidateerror',
            () => peer.connectionState
        );
        peer.oniceconnectionstatechange = logEvent(
            'oniceconnectionstatechange',
            () => peer.connectionState
        );
        peer.onicegatheringstatechange = logEvent(
            'onicegatheringstatechange',
            () => peer.connectionState
        );
        peer.onnegotiationneeded = logEvent(
            'onnegotiationneeded',
            () => peer.connectionState
        );
        peer.onsignalingstatechange = logEvent(
            'onsignalingstatechange',
            () => peer.connectionState
        );

        peer.addEventListener('icecandidate', async ev => {
            if (!ev.candidate || ev.candidate.candidate === '') return;

            const candidate = ev.candidate;

            [...this.candidateListeners].forEach(listener => {
                listener(candidate);
            });
        });

        peer.addEventListener('track', async ev => {
            const stream = ev.streams[0];

            [...this.streamListeners].forEach(listener => listener(stream, ev.track.kind));
        });
    }

    async acceptOffer(desc: RTCSessionDescriptionInit) {
        this.peer.setRemoteDescription(desc);
    }

    async createAnswer() {
        const desc = await this.peer.createAnswer();

        this.peer.setLocalDescription(desc); // intentionally non-awaited

        return desc;
    }

    async createOffer(localStream: MediaStream) {
        localStream.getTracks().forEach(track => {
            this.peer?.addTrack(track, localStream);
        });

        const localSdp = await this.peer.createOffer();

        await this.peer.setLocalDescription(localSdp);

        return localSdp;
    }

    async acceptAnswer(jsep: RTCSessionDescriptionInit) {
        await this.peer?.setRemoteDescription(jsep);
    }

    async getStats(selector?: MediaStreamTrack): Promise<RTCStatsReport | undefined> {
        return await this.peer?.getStats(selector);
    }

    addStream(stream: MediaStream) {
        stream.getTracks().forEach(track => {
            this.peer.addTrack(track, stream);
        });
    }

    addCandidateListener(listener: (candidate: RTCIceCandidate) => void) {
        this.candidateListeners.push(listener);
    }

    addStreamListener(listener: (stream: MediaStream, kind: string) => void) {
        this.streamListeners.push(listener);
    }

    clearStreamListener() {
        // do nothing
    }

    getReceivers() {
        return this.peer.getReceivers();
    }
}
